import React, { useState } from "react";
import { MoreHorizontal, Eye, Pencil, Archive, AlertTriangle, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * ActionMenu — shared •••  overflow. Reuses existing archive endpoints via
 * the caller-provided handlers. Never hard-deletes.
 *
 * `customActions` slot (Holds "Release", etc.):
 *   [{ key, label, onClick, icon, danger, disabled, testid }]
 * Rendered ABOVE the archive item, separated by a divider so destructive
 * "Archive" stays last and visually distinct.
 */
export const ActionMenu = ({
  onView,
  onEdit,
  onArchive,
  onHardDelete,
  archiveLabel = "Archive",
  customActions = [],
  disabled = false,
  size = "sm",
  testid = "action-menu",
  align = "end",
}) => {
  const btnClass =
    size === "sm"
      ? "w-8 h-8 grid place-items-center rounded-md"
      : "w-9 h-9 grid place-items-center rounded-md";
  const hasTopSection = !!(onView || onEdit || customActions.length > 0);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          type="button"
          className={btnClass + " text-[color:var(--fg-secondary)] hover:text-[color:var(--fg-primary)] hover:bg-[color:var(--bg-inset)] transition-colors"}
          onClick={(e) => e.stopPropagation()}
          data-testid={testid}
          aria-label="Actions"
        >
          <MoreHorizontal size={18} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className="min-w-[160px]"
        data-testid={`${testid}-menu`}
      >
        {onView && (
          <DropdownMenuItem onClick={onView} data-testid={`${testid}-view`}>
            <Eye size={14} className="mr-2" /> View
          </DropdownMenuItem>
        )}
        {onEdit && (
          <DropdownMenuItem onClick={onEdit} data-testid={`${testid}-edit`}>
            <Pencil size={14} className="mr-2" /> Edit
          </DropdownMenuItem>
        )}
        {customActions.map((a) => (
          <DropdownMenuItem
            key={a.key}
            onClick={a.onClick}
            disabled={a.disabled}
            data-testid={a.testid || `${testid}-${a.key}`}
            className={a.danger ? "text-[color:var(--status-critical,#B83C3C)] focus:text-[color:var(--status-critical,#B83C3C)]" : ""}
          >
            {a.icon ? <span className="mr-2 inline-flex">{a.icon}</span> : null}
            {a.label}
          </DropdownMenuItem>
        ))}
        {onArchive && hasTopSection && <DropdownMenuSeparator />}
        {onArchive && (
          <DropdownMenuItem
            onClick={onArchive}
            className="text-[color:var(--status-critical,#B83C3C)] focus:text-[color:var(--status-critical,#B83C3C)]"
            data-testid={`${testid}-archive`}
          >
            <Archive size={14} className="mr-2" /> {archiveLabel}
          </DropdownMenuItem>
        )}
        {onHardDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onHardDelete}
              className="text-[color:var(--status-critical,#B83C3C)] focus:text-[color:var(--status-critical,#B83C3C)] font-medium"
              data-testid={`${testid}-hard-delete`}
            >
              <AlertTriangle size={14} className="mr-2" /> Delete permanently…
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * ArchiveConfirmModal — safe-delete confirmation. Uses the archive verb
 * consistently and preserves historical relationships.
 * If `blockers` (array of strings) is non-empty, shows the hard-dependency
 * fallback instead of the primary archive CTA.
 */
export const ArchiveConfirmModal = ({
  open,
  entityLabel,
  entityName,
  description,
  blockers = [],
  loading = false,
  onCancel,
  onConfirm,
}) => {
  if (!open) return null;
  const blocked = blockers && blockers.length > 0;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="archive-modal">
      <button
        aria-label="Close"
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.55)" }}
        onClick={onCancel}
      />
      <div
        className="relative w-full max-w-md rounded-md overflow-hidden"
        style={{ background: "var(--bg-elevated)", border: "1px solid var(--border-default)" }}
      >
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border-subtle)" }}>
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} style={{ color: blocked ? "#D79A27" : "#B83C3C" }} />
            <div className="text-[15px] font-semibold" style={{ color: "var(--fg-primary)" }}>
              {blocked ? "Cannot Delete" : `Archive ${entityLabel}?`}
            </div>
          </div>
          <button onClick={onCancel} className="w-8 h-8 grid place-items-center rounded-md"
                  style={{ color: "var(--fg-muted)" }} data-testid="archive-close">
            <X size={18} />
          </button>
        </div>
        <div className="p-5">
          {blocked ? (
            <>
              <div className="text-[13.5px]" style={{ color: "var(--fg-primary)" }}>
                <strong>{entityName}</strong> cannot be permanently deleted because it&apos;s referenced by:
              </div>
              <ul className="mt-3 space-y-1 text-[13px]" style={{ color: "var(--fg-secondary)" }}>
                {blockers.map((b, i) => (
                  <li key={i} className="pl-3 border-l-2" style={{ borderColor: "var(--border-strong)" }}>{b}</li>
                ))}
              </ul>
              <div className="mt-3 text-[13px]" style={{ color: "var(--fg-secondary)" }}>
                You can still archive it — historical relationships and audit records will be preserved.
              </div>
            </>
          ) : (
            <>
              <div className="text-[14px]" style={{ color: "var(--fg-primary)" }}>
                Archive <strong>{entityName}</strong>?
              </div>
              <div className="mt-2 text-[13px]" style={{ color: "var(--fg-secondary)" }}>
                {description || "It will be removed from active lists. Historical relationships and audit records are preserved."}
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-4 border-t flex items-center justify-end gap-2" style={{ borderColor: "var(--border-subtle)" }}>
          <button
            type="button"
            onClick={onCancel}
            data-testid="archive-cancel"
            className="h-10 px-4 rounded-md text-[13px] font-medium"
            style={{ color: "var(--fg-secondary)" }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            data-testid="archive-confirm"
            className="h-10 px-5 rounded-md text-[13px] font-semibold disabled:opacity-60"
            style={{
              background: blocked ? "#D79A27" : "#B83C3C",
              color: "#FFFFFF",
            }}
          >
            {loading ? "Archiving…" : blocked ? "Archive Instead" : `Archive ${entityLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * useArchive — small hook that wraps the archive endpoint + confirm modal +
 * toast + optional reload callback. Wire it into any list/detail page.
 */
export const useArchive = ({ entityLabel, apiArchive, onSuccess, description, checkBlockers }) => {
  const [target, setTarget] = useState(null);
  const [loading, setLoading] = useState(false);
  const request = async (row) => {
    if (checkBlockers) {
      try {
        const blockers = await checkBlockers(row);
        setTarget({ ...row, __blockers: blockers || [] });
      } catch (e) {
        setTarget(row);
      }
    } else {
      setTarget(row);
    }
  };
  const cancel = () => setTarget(null);
  const confirm = async () => {
    if (!target) return;
    setLoading(true);
    try {
      if (target.__blockers && target.__blockers.length > 0) {
        // Blocked branch: archive is still allowed (soft), just show the
        // reason first. Proceed with archive on confirm.
        await apiArchive(target.id);
      } else {
        await apiArchive(target.id);
      }
      if (onSuccess) await onSuccess(target);
      setTarget(null);
    } finally {
      setLoading(false);
    }
  };
  return {
    request,
    modalProps: {
      open: !!target,
      entityLabel,
      description,
      entityName: target?.display_name || target?.name || target?.label || target?.id || "",
      blockers: target?.__blockers || [],
      loading,
      onCancel: cancel,
      onConfirm: confirm,
    },
  };
};
